import { useState, useEffect } from 'react';
import {
    X, Crop, UserPlus, Loader2, Copy, CheckCircle2, Send, UserMinus,
    Plus, Edit2, Trash2, Save, Check, Palette, Store, Image as ImageIcon,
    MessageSquare, HelpCircle, ScrollText, Phone, Shield, AlertTriangle,
    Sparkles, Wand2, Gift, Users, Ticket,
} from 'lucide-react';
import { updateStore, deleteStore, createStoreInvite, removeCoAdmin, useStoreFAQ, createFAQ, updateFAQ, deleteFAQ, useStoreClubMembers } from '../hooks/useStoreApi';
import { navigate } from '../App';
import { StoreImageCropModal } from './StoreImageCropModal';
import { STORE_THEMES, DEFAULT_STORE_THEME_ID } from '../themes/storeThemes';
import { getStoreThemeSwatch } from '../themes/applyStoreTheme';
import { resolveDesign, VIBES, matchesVibe, findFontPair } from '../themes/storeDesign';
import { DesignStudio, WelcomeStylePicker } from './store/admin';
import { Card, Input, Textarea, Label, HelpText, Button, Badge } from './ui';
import { CLUB_BENEFIT_TYPES, clubBenefitDescription, hasClubBenefit } from '../utils/clubBenefit';

// Short Hebrew date+time for the referral redemption log.
const formatRedemptionDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/* --------------------------------------------------------------------------
 * Small presentational helpers — one consistent visual language for every
 * section. No more per-section rainbow of background/border colors.
 * ------------------------------------------------------------------------ */

// A titled block inside a section. Neutral surface, icon chip accent.
const Block = ({ icon: Icon, title, description, children, tone = 'brand' }) => {
    const chip = {
        brand: 'bg-brand-50 text-brand-600 ring-brand-100',
        info: 'bg-info-50 text-info-600 ring-info-100',
        warning: 'bg-warning-50 text-warning-600 ring-warning-100',
        success: 'bg-success-50 text-success-600 ring-success-100',
        danger: 'bg-danger-50 text-danger-600 ring-danger-100',
    }[tone] || 'bg-brand-50 text-brand-600 ring-brand-100';
    return (
        <section className="space-y-4">
            <div className="flex items-start gap-3">
                {Icon && (
                    <span className={`shrink-0 p-2.5 rounded-xl ring-1 ${chip}`}>
                        <Icon className="w-5 h-5" />
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-ink-strong">{title}</h3>
                    {description && <p className="text-sm text-ink-muted mt-0.5 leading-relaxed">{description}</p>}
                </div>
            </div>
            <div className="space-y-4">{children}</div>
        </section>
    );
};

// Field wrapper: label + control + optional help line.
const Field = ({ label, htmlFor, required, help, children }) => (
    <div>
        {label && <Label htmlFor={htmlFor} required={required}>{label}</Label>}
        {children}
        {help && <HelpText>{help}</HelpText>}
    </div>
);

// Store Details Tab (extracted from StoreAdmin.jsx)
// Props: store, refetchStore, onRegisterRefresh, chatId, isActualOwner
export const StoreDetailsTab = ({ store, refetchStore, onRegisterRefresh, chatId, isActualOwner = true }) => {
    const [formData, setFormData] = useState({
        name: store?.name || '',
        about: store?.about || '',
        profileImage: null,
        coverImage: null,
        aboutImage: null,
        removeAboutImage: false,
        backgroundImage: null,
        removeBackgroundImage: false,
        backgroundImagePortrait: null,
        removeBackgroundImagePortrait: false,
        isClosed: !!(store?.is_closed || store?.closed || store?.status === 'closed'),
        popupTitle: store?.popup_title || '',
        popupDescription: store?.popup_description || '',
        popupStyle: store?.popup_style || 'classic',
        contactPhone: store?.contact_phone || '',
        whatsappAvailable: !!(store?.whatsapp_available),
        purchaseConfirmationMessage: store?.purchase_confirmation_message || '',
        cancellationPolicy: store?.cancellation_policy || '',
        clubBenefit: store?.club_benefit || { type: '' },
        themeId: store?.theme_id || DEFAULT_STORE_THEME_ID,
        designConfig: resolveDesign(store)
    });
    // Studio overlay + local-edit guard so a refetch doesn't clobber an
    // in-progress design before it's saved.
    const [studioOpen, setStudioOpen] = useState(false);
    const [designDirty, setDesignDirty] = useState(false);
    // Track if the admin has edited the purchase confirmation message locally
    const [purchaseMessageDirty, setPurchaseMessageDirty] = useState(false);
    // Track if the admin has edited the cancellation policy locally
    const [cancellationPolicyDirty, setCancellationPolicyDirty] = useState(false);
    // Track if the admin has edited the customers-club benefit locally
    const [clubBenefitDirty, setClubBenefitDirty] = useState(false);
    const [profileImagePreview, setProfileImagePreview] = useState(store?.profile_image || null);
    const [coverImagePreview, setCoverImagePreview] = useState(store?.cover_image || null);
    const [aboutImagePreview, setAboutImagePreview] = useState(store?.about_image_url || store?.about_image || null);
    const [backgroundImagePreview, setBackgroundImagePreview] = useState(store?.background_image_url || store?.background_image || null);
    const [backgroundImagePortraitPreview, setBackgroundImagePortraitPreview] = useState(store?.background_image_portrait_url || store?.background_image_portrait || null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false); // track delete action
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [tempImageSrc, setTempImageSrc] = useState(null);
    const [cropImageType, setCropImageType] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false); // updating open/closed state immediately

    // Invitation state
    const [generatingInvite, setGeneratingInvite] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [inviteExpiresAt, setInviteExpiresAt] = useState(null);

    // Remove co-admin state
    const [removingCoAdmin, setRemovingCoAdmin] = useState(null);

    // Sub-tab state
    const [activeSubTab, setActiveSubTab] = useState('general');

    // FAQ state
    const { data: faqs = [], refetch: refetchFAQs } = useStoreFAQ(store?.store_slug);
    const [faqList, setFaqList] = useState([]);
    const [editingFaqId, setEditingFaqId] = useState(null);
    const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
    const [faqLoading, setFaqLoading] = useState(false);
    const [faqError, setFaqError] = useState(null);

    // Customers-club members (read-only list for the owner)
    const { data: clubData, loading: clubMembersLoading } = useStoreClubMembers(store?.store_id, chatId);
    const clubMembers = clubData?.members || [];
    const clubRedemptions = clubData?.redemptions || [];
    const isReferralClub = !!clubData?.is_referral;

    const clubBenefit = formData.clubBenefit || {};
    const setBenefitType = (type) => {
        setClubBenefitDirty(true);
        setFormData(fd => ({ ...fd, clubBenefit: { type } }));
    };
    const setBenefitField = (patch) => {
        setClubBenefitDirty(true);
        setFormData(fd => ({ ...fd, clubBenefit: { ...(fd.clubBenefit || {}), ...patch } }));
    };
    const setBenefitNumber = (key, value) => {
        const num = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
        setBenefitField({ [key]: num });
    };

    // Register refresh handler (just refetch store data)
    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('store-details', async () => {
                await refetchStore?.();
            });
        }
    }, [onRegisterRefresh, refetchStore]);

    // Update previews if store changes (e.g. after external refresh)
    useEffect(() => {
        setProfileImagePreview(store?.owner_profile_url || store?.profile_image || null);
        setCoverImagePreview(store?.cover_image_url || store?.cover_image || null);
        setAboutImagePreview(store?.about_image_url || store?.about_image || null);
        setBackgroundImagePreview(store?.background_image_url || store?.background_image || null);
        setBackgroundImagePortraitPreview(store?.background_image_portrait_url || store?.background_image_portrait || null);
        setFormData(fd => ({
            ...fd,
            name: store?.name || '',
            about: store?.about || '',
            isClosed: !!(store?.is_closed || store?.closed || store?.status === 'closed'),
            contactPhone: store?.contact_phone || '',
            whatsappAvailable: !!(store?.whatsapp_available),
            // Only update from store if the field hasn't been edited locally to avoid overwriting user input
            purchaseConfirmationMessage: purchaseMessageDirty
                ? fd.purchaseConfirmationMessage
                : (store?.purchase_confirmation_message ?? fd.purchaseConfirmationMessage),
            cancellationPolicy: cancellationPolicyDirty
                ? fd.cancellationPolicy
                : (store?.cancellation_policy ?? fd.cancellationPolicy),
            clubBenefit: clubBenefitDirty
                ? fd.clubBenefit
                : (store?.club_benefit ?? fd.clubBenefit),
            themeId: store?.theme_id || fd.themeId || DEFAULT_STORE_THEME_ID,
            designConfig: designDirty ? fd.designConfig : resolveDesign(store)
        }));
    }, [store?.owner_profile_url, store?.profile_image, store?.cover_image_url, store?.cover_image, store?.about_image_url, store?.about_image, store?.background_image_url, store?.background_image, store?.background_image_portrait_url, store?.background_image_portrait, store?.name, store?.about, store?.contact_phone, store?.whatsapp_available, store?.is_closed, store?.closed, store?.status, store?.purchase_confirmation_message, store?.cancellation_policy, store?.club_benefit, store?.theme_id, store?.design_config]);

    // Sync FAQs from API
    useEffect(() => {
        if (faqs && Array.isArray(faqs)) {
            setFaqList(faqs);
        }
    }, [faqs]);

    const handleImageChange = (e, imageType) => {
        const input = e.target;
        const file = input.files && input.files[0];
        // Always clear the input value so re-selecting the SAME file (after
        // cancelling the crop, removing the preview, or a validation error)
        // still fires `change`. The File reference is already captured above.
        input.value = '';
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError('גודל התמונה גדול מדי. מקסימום 10MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError('יש להעלות קובץ תמונה בלבד');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setTempImageSrc(reader.result);
            setCropImageType(imageType);
            setShowCropModal(true);
            setError(null);
            setSuccess(null);
        };
        reader.onerror = () => {
            setError('שגיאה בקריאת קובץ התמונה. נסה שוב.');
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = (croppedImage) => {
        if (cropImageType === 'profile') {
            setFormData(fd => ({ ...fd, profileImage: croppedImage }));
            setProfileImagePreview(croppedImage);
        } else if (cropImageType === 'cover') {
            setFormData(fd => ({ ...fd, coverImage: croppedImage }));
            setCoverImagePreview(croppedImage);
        } else if (cropImageType === 'about') {
            setFormData(fd => ({ ...fd, aboutImage: croppedImage, removeAboutImage: false }));
            setAboutImagePreview(croppedImage);
        } else if (cropImageType === 'background') {
            setFormData(fd => ({ ...fd, backgroundImage: croppedImage, removeBackgroundImage: false }));
            setBackgroundImagePreview(croppedImage);
        } else if (cropImageType === 'background-portrait') {
            setFormData(fd => ({ ...fd, backgroundImagePortrait: croppedImage, removeBackgroundImagePortrait: false }));
            setBackgroundImagePortraitPreview(croppedImage);
        }
        setShowCropModal(false);
        setTempImageSrc(null);
        setCropImageType(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const updateData = { name: formData.name, about: formData.about };
            if (formData.profileImage) updateData.profile_image_base64 = formData.profileImage;
            if (formData.coverImage) updateData.cover_image_base64 = formData.coverImage;
            if (formData.aboutImage) {
                updateData.about_image_base64 = formData.aboutImage;
            } else if (formData.removeAboutImage) {
                updateData.about_image = '';
            }
            if (formData.backgroundImage) {
                updateData.background_image_base64 = formData.backgroundImage;
            } else if (formData.removeBackgroundImage) {
                updateData.background_image = '';
            }
            if (formData.backgroundImagePortrait) {
                updateData.background_image_portrait_base64 = formData.backgroundImagePortrait;
            } else if (formData.removeBackgroundImagePortrait) {
                updateData.background_image_portrait = '';
            }
            // Add closed/open status (backend expected key assumed: is_closed)
            updateData.is_closed = !!formData.isClosed;
            // Popup message (title + description). Send even if empty to allow clearing.
            updateData.popup_title = formData.popupTitle || '';
            updateData.popup_description = formData.popupDescription || '';
            // Welcome modal presentation style ('classic' or an animated preset id)
            updateData.popup_style = formData.popupStyle || 'classic';
            // Contact info (phone + whatsapp availability)
            updateData.contact_phone = formData.contactPhone || '';
            updateData.whatsapp_available = !!formData.whatsappAvailable;
            // Purchase confirmation message: ALWAYS send (backend will decide if update is needed)
            updateData.purchase_confirmation_message = formData.purchaseConfirmationMessage || '';
            // Cancellation policy: send unconditionally so clearing also persists
            updateData.cancellation_policy = formData.cancellationPolicy || '';

            updateData.club_benefit = (formData.clubBenefit && formData.clubBenefit.type)
                ? formData.clubBenefit
                : {};
            // Full storefront design (palette + structure). theme_id is kept
            // in sync for backward-compatible consumers.
            const dc = resolveDesign(formData.designConfig);
            updateData.design_config = dc;
            updateData.theme_id = dc.themeId || formData.themeId || DEFAULT_STORE_THEME_ID;
            await updateStore(store.store_id, updateData, chatId);
            await refetchStore?.();
            setSuccess('החנות עודכנה בהצלחה');
            setCancellationPolicyDirty(false);
            setDesignDirty(false);
            // Clear image temp data after success
            setFormData(fd => ({ ...fd, profileImage: null, coverImage: null, aboutImage: null, removeAboutImage: false, backgroundImage: null, removeBackgroundImage: false, backgroundImagePortrait: null, removeBackgroundImagePortrait: false }));
        } catch (err) {
            setError(err.message || 'שגיאה בעדכון החנות');
        } finally {
            setLoading(false);
        }
    };

    // Immediate update when toggling store open/close state
    const handleStoreStatusChange = async (closed) => {
        if (!store?.store_id) return;
        // Optimistically update UI
        setFormData(fd => ({ ...fd, isClosed: closed }));
        if (statusUpdating) return; // prevent concurrent updates
        setStatusUpdating(true);
        setError(null);
        setSuccess(null);
        try {
            // Send multiple synonymous keys to maximize backend compatibility across lambdas (is_closed, status, closed)
            await updateStore(store.store_id, {
                is_closed: !!closed,
                status: closed ? 'closed' : 'open',
                closed: !!closed
            }, chatId);
            await refetchStore?.();
            setSuccess(closed ? 'החנות נסגרה להזמנות חדשות' : 'החנות נפתחה להזמנות');
            // Auto-clear success after a delay
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setError(err.message || 'שגיאה בעדכון מצב החנות');
        } finally {
            setStatusUpdating(false);
        }
    };

    // Delete store handler (danger action)
    const handleDeleteStore = async () => {
        if (!store?.store_id) return;
        if (!window.confirm('אזהרה: פעולה זו תמחק את החנות וכל המוצרים שלה. האם אתה בטוח?')) {
            return;
        }
        try {
            setDeleting(true);
            await deleteStore(store.store_id, chatId);
            alert('החנות נמחקה בהצלחה');
            // After deletion, redirect user back to zones (user zones view)
            navigate('zones');
        } catch (err) {
            alert('שגיאה במחיקת החנות: ' + (err?.message || 'שגיאה לא ידועה'));
        } finally {
            setDeleting(false);
        }
    };

    // Generate invitation link handler
    const handleGenerateInvite = async () => {
        if (!store?.store_id) return;
        setGeneratingInvite(true);
        setInviteError(null);
        setInviteLink('');
        setInviteLinkCopied(false);

        try {
            const response = await createStoreInvite(store.store_id, null, chatId);
            setInviteLink(response.invite_link);
            setInviteExpiresAt(response.expires_at);
            setSuccess('קישור הזמנה נוצר בהצלחה! תוקף: 5 דקות');
            // Auto-clear success after delay
            setTimeout(() => setSuccess(null), 5000);
        } catch (err) {
            setInviteError(err?.message || 'שגיאה ביצירת הזמנה');
        } finally {
            setGeneratingInvite(false);
        }
    };

    // Copy invitation link to clipboard
    const handleCopyInviteLink = async () => {
        if (!inviteLink) return;
        try {
            await navigator.clipboard.writeText(inviteLink);
            setInviteLinkCopied(true);
            setTimeout(() => setInviteLinkCopied(false), 3000);
        } catch (err) {
            setInviteError('שגיאה בהעתקת הקישור');
        }
    };

    // Remove co-admin handler
    const handleRemoveCoAdmin = async (adminUserId, adminName) => {
        if (!store?.store_id) return;

        if (!window.confirm(`האם אתה בטוח שברצונך להסיר את ${adminName} מתפקיד מנהל משנה?`)) {
            return;
        }

        setRemovingCoAdmin(adminUserId);
        setError(null);
        setSuccess(null);

        try {
            await removeCoAdmin(store.store_id, adminUserId, chatId);
            await refetchStore?.();
            setSuccess(`${adminName} הוסר בהצלחה מתפקיד מנהל משנה`);
            // Auto-clear success after delay
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setError(err?.message || 'שגיאה בהסרת מנהל משנה');
        } finally {
            setRemovingCoAdmin(null);
        }
    };

    // FAQ Handlers
    const handleAddFaq = async () => {
        if (!newFaq.question.trim() || !newFaq.answer.trim()) {
            setFaqError('יש למלא שאלה ותשובה');
            return;
        }

        setFaqLoading(true);
        setFaqError(null);

        try {
            await createFAQ(store.store_id, {
                question: newFaq.question,
                answer: newFaq.answer,
                sort_order: faqList.length
            }, chatId);

            await refetchFAQs();
            setNewFaq({ question: '', answer: '' });
            setSuccess('שאלה נוספה בהצלחה');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setFaqError(err?.message || 'שגיאה בהוספת שאלה');
        } finally {
            setFaqLoading(false);
        }
    };

    const handleUpdateFaq = async (faqId, updatedData) => {
        setFaqLoading(true);
        setFaqError(null);

        try {
            await updateFAQ(store.store_id, faqId, updatedData, chatId);
            await refetchFAQs();
            setEditingFaqId(null);
            setSuccess('שאלה עודכנה בהצלחה');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setFaqError(err?.message || 'שגיאה בעדכון שאלה');
        } finally {
            setFaqLoading(false);
        }
    };

    const handleDeleteFaq = async (faqId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק שאלה זו?')) {
            return;
        }

        setFaqLoading(true);
        setFaqError(null);

        try {
            await deleteFAQ(store.store_id, faqId, chatId);
            await refetchFAQs();
            setSuccess('שאלה נמחקה בהצלחה');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setFaqError(err?.message || 'שגיאה במחיקת שאלה');
        } finally {
            setFaqLoading(false);
        }
    };

    const sections = [
        { id: 'general', label: 'כללי', sub: 'שם, תיאור ומצב', icon: Store },
        { id: 'images', label: 'תמונות', sub: 'פרופיל וכיסוי', icon: ImageIcon },
        { id: 'theme', label: 'מראה החנות', sub: 'סטודיו העיצוב', icon: Sparkles },
        { id: 'messages', label: 'הודעות ללקוח', sub: 'הודעה קופצת ואישור', icon: MessageSquare },
        { id: 'faq', label: 'שאלות נפוצות', sub: 'שו"ת ללקוחות', icon: HelpCircle },
        { id: 'cancellation', label: 'מדיניות ביטול', sub: 'תנאי ביטול והחזר', icon: ScrollText },
        { id: 'club', label: 'מועדון לקוחות', sub: 'הטבות ולקוחות רשומים', icon: Gift },
        { id: 'contact', label: 'קשר והתראות', sub: 'טלפון, וואטסאפ, טלגרם', icon: Phone },
        ...(isActualOwner ? [{ id: 'management', label: 'ניהול וגישה', sub: 'מנהלי משנה', icon: Shield }] : [])
    ];

    const activeSection = sections.find(s => s.id === activeSubTab) || sections[0];

    // The save bar is meaningful for sections that edit the store form.
    const SAVE_SECTIONS = new Set(['general', 'images', 'theme', 'messages', 'cancellation', 'club']);
    const showSaveBar = SAVE_SECTIONS.has(activeSubTab);

    const inputCls =
        'w-full px-3.5 py-2.5 bg-surface border border-line rounded-lg text-ink-strong placeholder:text-ink-faint ' +
        'transition-[border-color,box-shadow] duration-200 hover:border-line-strong ' +
        'focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/25';

    return (
        <div className="space-y-5 animate-fade-in pb-24 lg:pb-10" dir="rtl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                <div>
                    <h2 className="text-2xl font-bold text-ink-strong">הגדרות החנות</h2>
                    <p className="text-sm text-ink-muted mt-1">נהל את פרטי החנות, המראה והתוכן שהלקוחות רואים</p>
                </div>
                <Badge
                    variant={formData.isClosed ? 'danger' : 'success'}
                    size="md"
                    dot
                >
                    {formData.isClosed ? 'החנות סגורה להזמנות' : 'החנות פתוחה'}
                </Badge>
            </div>

            {/* Inline status messages */}
            {error && (
                <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl text-danger-800 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            {success && (
                <div className="p-4 bg-success-50 border border-success-200 rounded-xl text-success-800 text-sm flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{success}</span>
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-start">
                {/* Section rail — horizontal scroll on mobile, sticky vertical on desktop */}
                <nav
                    aria-label="הגדרות"
                    className="w-full lg:w-60 shrink-0 lg:sticky lg:top-20"
                >
                    <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-1 px-1">
                        {sections.map((s) => {
                            const Icon = s.icon;
                            const active = activeSubTab === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setActiveSubTab(s.id)}
                                    aria-current={active ? 'page' : undefined}
                                    className={`group flex items-center gap-3 shrink-0 lg:w-full text-right rounded-xl px-3.5 py-3 transition-all duration-200 ${
                                        active
                                            ? 'bg-brand-500 text-ink-inverse shadow-sm'
                                            : 'bg-surface lg:bg-transparent text-ink-muted hover:bg-surface-muted hover:text-ink border border-line-subtle lg:border-transparent'
                                    }`}
                                >
                                    <span
                                        className={`shrink-0 ${
                                            active ? 'text-ink-inverse' : 'text-ink-subtle group-hover:text-brand-600'
                                        }`}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-sm font-semibold whitespace-nowrap lg:whitespace-normal">{s.label}</span>
                                        <span className={`hidden lg:block text-xs truncate ${active ? 'text-ink-inverse/75' : 'text-ink-faint'}`}>
                                            {s.sub}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Content panel */}
                <div className="flex-1 min-w-0 w-full space-y-5">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <Card variant="surface" padding="lg" radius="2xl" className="space-y-8">
                            {/* ---------------------------------------------- GENERAL */}
                            {activeSubTab === 'general' && (
                                <Block icon={Store} title="פרטים כלליים" description="המידע הבסיסי שמזהה את החנות שלך">
                                    {/* Open / closed status */}
                                    <div className="rounded-xl border border-line bg-surface-muted p-4">
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-ink">מצב החנות</p>
                                                <p className="text-xs text-ink-muted mt-0.5">סגירה חוסמת הזמנות חדשות אך משאירה את החנות גלויה. נשמר מיד.</p>
                                            </div>
                                            <div className="inline-flex rounded-lg border border-line-strong overflow-hidden bg-surface">
                                                <button
                                                    type="button"
                                                    aria-pressed={!formData.isClosed}
                                                    onClick={() => handleStoreStatusChange(false)}
                                                    disabled={statusUpdating || !store?.store_id || !formData.isClosed}
                                                    className={`px-4 py-2 text-sm font-semibold transition-all ${!formData.isClosed ? 'bg-success-500 text-ink-inverse' : 'text-ink hover:bg-surface-muted'} ${statusUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                >{statusUpdating && !formData.isClosed ? 'מעדכן…' : 'פתוח'}</button>
                                                <button
                                                    type="button"
                                                    aria-pressed={formData.isClosed}
                                                    onClick={() => handleStoreStatusChange(true)}
                                                    disabled={statusUpdating || !store?.store_id || formData.isClosed}
                                                    className={`px-4 py-2 text-sm font-semibold transition-all border-r border-line-strong ${formData.isClosed ? 'bg-danger-500 text-ink-inverse' : 'text-ink hover:bg-surface-muted'} ${statusUpdating ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                >{statusUpdating && formData.isClosed ? 'מעדכן…' : 'סגור'}</button>
                                            </div>
                                        </div>
                                    </div>

                                    <Field label="שם החנות" htmlFor="sd-name" required>
                                        <Input
                                            id="sd-name"
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </Field>

                                    <Field label="תיאור החנות" htmlFor="sd-about" help="תיאור קצר שמופיע ללקוחות בעמוד החנות">
                                        <Textarea
                                            id="sd-about"
                                            value={formData.about}
                                            onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                                            rows={4}
                                            placeholder="ספר על החנות שלך…"
                                        />
                                    </Field>

                                    <div className="space-y-3">
                                        <Label>
                                            תמונה לעמוד "אודות"{' '}
                                            <span className="text-xs font-normal text-ink-subtle">(אופציונלי)</span>
                                        </Label>
                                        {aboutImagePreview && (
                                            <div className="relative inline-block max-w-full rounded-lg overflow-hidden border-2 border-line-strong bg-surface-muted">
                                                <img
                                                    src={aboutImagePreview}
                                                    alt="About"
                                                    className="max-w-full max-h-80 object-contain"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setAboutImagePreview(null);
                                                        setFormData(fd => ({
                                                            ...fd,
                                                            aboutImage: null,
                                                            // If the store already had a saved image, mark it
                                                            // for removal so "שמור שינויים" clears it server-side.
                                                            removeAboutImage: !!(store?.about_image_url || store?.about_image),
                                                        }));
                                                    }}
                                                    className="absolute top-2 right-2 p-2 bg-danger-500 text-white rounded-full hover:bg-danger-600 shadow-md"
                                                    title="הסר תמונה"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                        <label className="block cursor-pointer">
                                            <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 hover:bg-brand-50/40 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                                <Crop className="w-4 h-4" />
                                                <span className="text-sm">
                                                    {aboutImagePreview ? 'שנה תמונה' : 'העלה תמונה'}
                                                </span>
                                            </div>
                                            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'about')} className="hidden" />
                                        </label>
                                        <HelpText>התמונה תוצג ללקוחות בלשונית "אודות". מומלץ יחס 16:9, דחיסה אוטומטית.</HelpText>
                                    </div>
                                </Block>
                            )}

                            {/* ---------------------------------------------- IMAGES */}
                            {activeSubTab === 'images' && (
                                <Block icon={ImageIcon} title="תמונות החנות" description="התמונות מעצבות את הרושם הראשוני בעמוד החנות">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Profile image */}
                                        <div className="space-y-3">
                                            <Label>תמונת פרופיל <span className="text-xs font-normal text-ink-subtle">(אופציונלי)</span></Label>
                                            <div className="flex items-center gap-4">
                                                {(profileImagePreview || store?.owner_profile_url || store?.profile_image) && (
                                                    <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-line-strong shrink-0">
                                                        <img
                                                            src={profileImagePreview || store?.owner_profile_url || store?.profile_image}
                                                            alt="Profile"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        {profileImagePreview && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setProfileImagePreview(store?.owner_profile_url || store?.profile_image || null);
                                                                    setFormData({ ...formData, profileImage: null });
                                                                }}
                                                                className="absolute top-1 right-1 p-1 bg-danger-500 text-white rounded-full hover:bg-danger-600 shadow-md"
                                                                title="בטל שינוי"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                <label className="flex-1 cursor-pointer">
                                                    <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 hover:bg-brand-50/40 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                                        <Crop className="w-4 h-4" />
                                                        <span className="text-sm">
                                                            {(profileImagePreview || store?.owner_profile_url || store?.profile_image) ? 'שנה תמונה' : 'העלה תמונה'}
                                                        </span>
                                                    </div>
                                                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'profile')} className="hidden" />
                                                </label>
                                            </div>
                                            <HelpText>התמונה תיחתך לריבוע ותידחס אוטומטית</HelpText>
                                        </div>

                                        {/* Cover image */}
                                        <div className="space-y-3">
                                            <Label>תמונת כיסוי <span className="text-xs font-normal text-ink-subtle">(אופציונלי)</span></Label>
                                            {(coverImagePreview || store?.cover_image_url || store?.cover_image) && (
                                                <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-line-strong">
                                                    <img
                                                        src={coverImagePreview || store?.cover_image_url || store?.cover_image}
                                                        alt="Cover"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {coverImagePreview && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setCoverImagePreview(store?.cover_image_url || store?.cover_image || null);
                                                                setFormData({ ...formData, coverImage: null });
                                                            }}
                                                            className="absolute top-2 right-2 p-2 bg-danger-500 text-white rounded-full hover:bg-danger-600 shadow-md"
                                                            title="בטל שינוי"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <label className="block cursor-pointer">
                                                <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 hover:bg-brand-50/40 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                                    <Crop className="w-4 h-4" />
                                                    <span className="text-sm">
                                                        {(coverImagePreview || store?.cover_image_url || store?.cover_image) ? 'שנה תמונה' : 'העלה תמונה'}
                                                    </span>
                                                </div>
                                                <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'cover')} className="hidden" />
                                            </label>
                                            <HelpText>מומלץ יחס 16:9, דחיסה אוטומטית</HelpText>
                                        </div>
                                    </div>

                                    {/* Full-store background image — separate desktop & mobile */}
                                    <div className="border-t border-line-subtle pt-6 space-y-2">
                                        <p className="text-sm font-semibold text-ink">תמונת רקע לכל החנות</p>
                                        <p className="text-xs text-ink-muted">
                                            ניתן להעלות תמונה נפרדת למסך רחב (דסקטופ) ולמסך לאורך (מובייל). אם רק אחת הועלתה — היא תוצג בכל המכשירים.
                                        </p>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                                            {/* Landscape / desktop */}
                                            <div className="space-y-3">
                                                <Label>
                                                    מסך רחב — דסקטופ{' '}
                                                    <span className="text-xs font-normal text-ink-subtle">(לרוחב, אופציונלי)</span>
                                                </Label>
                                                {backgroundImagePreview && (
                                                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-line-strong bg-surface-muted">
                                                        <img
                                                            src={backgroundImagePreview}
                                                            alt="Background desktop"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setBackgroundImagePreview(null);
                                                                setFormData(fd => ({
                                                                    ...fd,
                                                                    backgroundImage: null,
                                                                    removeBackgroundImage: !!(store?.background_image_url || store?.background_image),
                                                                }));
                                                            }}
                                                            className="absolute top-2 right-2 p-2 bg-danger-500 text-white rounded-full hover:bg-danger-600 shadow-md"
                                                            title="הסר תמונה"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                                <label className="block cursor-pointer">
                                                    <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 hover:bg-brand-50/40 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                                        <Crop className="w-4 h-4" />
                                                        <span className="text-sm">
                                                            {backgroundImagePreview ? 'שנה תמונה' : 'העלה תמונה'}
                                                        </span>
                                                    </div>
                                                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'background')} className="hidden" />
                                                </label>
                                                <HelpText>מומלץ תמונה רחבה (לרוחב). תוצג במסכים רחבים.</HelpText>
                                            </div>

                                            {/* Portrait / mobile */}
                                            <div className="space-y-3">
                                                <Label>
                                                    מסך לאורך — מובייל{' '}
                                                    <span className="text-xs font-normal text-ink-subtle">(לאורך, אופציונלי)</span>
                                                </Label>
                                                {backgroundImagePortraitPreview && (
                                                    <div className="relative mx-auto w-40 aspect-[9/16] rounded-lg overflow-hidden border-2 border-line-strong bg-surface-muted">
                                                        <img
                                                            src={backgroundImagePortraitPreview}
                                                            alt="Background mobile"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setBackgroundImagePortraitPreview(null);
                                                                setFormData(fd => ({
                                                                    ...fd,
                                                                    backgroundImagePortrait: null,
                                                                    removeBackgroundImagePortrait: !!(store?.background_image_portrait_url || store?.background_image_portrait),
                                                                }));
                                                            }}
                                                            className="absolute top-2 right-2 p-2 bg-danger-500 text-white rounded-full hover:bg-danger-600 shadow-md"
                                                            title="הסר תמונה"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                                <label className="block cursor-pointer">
                                                    <div className="px-4 py-3 border-2 border-dashed border-line rounded-lg hover:border-brand-400 hover:bg-brand-50/40 transition-colors text-center flex items-center justify-center gap-2 text-ink-muted">
                                                        <Crop className="w-4 h-4" />
                                                        <span className="text-sm">
                                                            {backgroundImagePortraitPreview ? 'שנה תמונה' : 'העלה תמונה'}
                                                        </span>
                                                    </div>
                                                    <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'background-portrait')} className="hidden" />
                                                </label>
                                                <HelpText>מומלץ תמונה לאורך. תוצג בטלפונים ובמסכים צרים.</HelpText>
                                            </div>
                                        </div>
                                        <HelpText>התמונה נמתחת על כל רקע החנות מאחורי התוכן. מומלץ להשתמש בתמונה לא עמוסה כדי לשמור על קריאוּת.</HelpText>
                                    </div>
                                </Block>
                            )}

            {/* ---------------------------------------------- THEME / DESIGN */}
                            {activeSubTab === 'theme' && (() => {
                                const dc = resolveDesign(formData.designConfig);
                                const sw = getStoreThemeSwatch(dc.themeId);
                                const activeVibe = VIBES.find(v => matchesVibe(dc, v));
                                const themeName = STORE_THEMES.find(t => t.id === dc.themeId)?.name || '';
                                // Quick-vibe tiles: stage into the form (the
                                // save bar then persists with everything else).
                                const applyDesign = (cfg) => {
                                    setFormData(fd => ({ ...fd, designConfig: cfg, themeId: cfg.themeId }));
                                    setDesignDirty(true);
                                };
                                // Studio "Apply & publish": persist immediately
                                // so there is no hidden second step. Sends only
                                // the design fields (PUT merges server-side).
                                const persistDesign = async (cfg) => {
                                    setError(null);
                                    setSuccess(null);
                                    try {
                                        await updateStore(
                                            store.store_id,
                                            { design_config: cfg, theme_id: cfg.themeId },
                                            chatId,
                                        );
                                        setFormData(fd => ({ ...fd, designConfig: cfg, themeId: cfg.themeId }));
                                        setDesignDirty(false);
                                        await refetchStore?.();
                                        setSuccess('העיצוב נשמר ופורסם בחנות');
                                        setStudioOpen(false);
                                    } catch (e) {
                                        setError(e?.message || 'שמירת העיצוב נכשלה');
                                        throw e; // keep the studio open on failure
                                    }
                                };
                                return (
                                    <Block
                                        icon={Sparkles}
                                        title="סטודיו העיצוב"
                                        description='עצבו חנות ייחודית לחלוטין — פלטה, גופנים, פינות, מרקם ותנועה. בסטודיו "החל ופרסם" שומר ומפרסם מיד; שינוי וייב מהיר כאן דורש לחיצה על "שמור שינויים".'
                                    >
                                        {/* current design hero */}
                                        <div className="rounded-2xl border border-line overflow-hidden bg-surface">
                                            <div className="flex h-24">
                                                <div className="flex-1" style={{ background: sw.surfaceBase }} />
                                                <div className="flex-1" style={{ background: sw.brand }} />
                                                <div className="flex-1" style={{ background: sw.secondary }} />
                                                <div className="flex-1" style={{ background: sw.brandDark }} />
                                                <div className="flex-[2] grid place-items-center" style={{ background: sw.surface, color: sw.ink }}>
                                                    <span className="text-2xl font-bold" style={{ fontFamily: findFontPair(dc.fontPair).display }}>אבג Aa</span>
                                                </div>
                                            </div>
                                            <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-extrabold text-ink-strong">
                                                        {activeVibe ? activeVibe.name : 'עיצוב מותאם אישית'}
                                                    </p>
                                                    <p className="text-xs text-ink-subtle mt-0.5">
                                                        פלטה: {themeName} · גופן: {findFontPair(dc.fontPair).label} · פינות: {dc.radius}
                                                    </p>
                                                </div>
                                                <Button type="button" variant="primary" onClick={() => setStudioOpen(true)} className="gap-2">
                                                    <Wand2 className="w-4 h-4" />
                                                    פתחו את הסטודיו
                                                </Button>
                                            </div>
                                        </div>

                                        {/* quick vibes */}
                                        <div className="space-y-2">
                                            <p className="text-xs font-bold text-ink-subtle uppercase tracking-wide">וייבים מהירים</p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                {VIBES.slice(0, 6).map((v) => {
                                                    const vsw = getStoreThemeSwatch(v.themeId);
                                                    const sel = activeVibe?.id === v.id;
                                                    return (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            onClick={() => applyDesign(resolveDesign({ ...v, vibe: v.id }))}
                                                            title={v.tagline}
                                                            className={`text-right rounded-xl border-2 overflow-hidden transition-all ${
                                                                sel ? 'border-brand-500 ring-2 ring-brand-200' : 'border-line hover:border-brand-400 hover:shadow-sm'
                                                            }`}
                                                        >
                                                            <div className="flex h-9">
                                                                <div className="flex-1" style={{ background: vsw.surfaceBase }} />
                                                                <div className="flex-1" style={{ background: vsw.brand }} />
                                                                <div className="flex-1" style={{ background: vsw.secondary }} />
                                                                <div className="flex-1" style={{ background: vsw.brandDark }} />
                                                            </div>
                                                            <div className="p-2 bg-surface flex items-center justify-between gap-1">
                                                                <span className="text-[11px] font-bold text-ink-strong truncate">{v.name}</span>
                                                                {sel && <Check className="w-3.5 h-3.5 text-brand-600 shrink-0" />}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-ink-faint">פתחו את הסטודיו לתצוגה חיה, ל-{VIBES.length} וייבים ולכוונון מלא של כל פרט.</p>
                                        </div>

                                        <DesignStudio
                                            open={studioOpen}
                                            store={store}
                                            value={formData.designConfig}
                                            onApply={persistDesign}
                                            onClose={() => setStudioOpen(false)}
                                        />
                                    </Block>
                                );
                            })()}

                            {/* ---------------------------------------------- MESSAGES */}
                            {activeSubTab === 'messages' && (
                                <div className="space-y-8">
                                    <Block
                                        icon={MessageSquare}
                                        title="הודעה קופצת בכניסה לחנות"
                                        description="תוצג ללקוחות כשנכנסים לחנות. השאר ריק כדי לא להציג הודעה."
                                    >
                                        <Field label="כותרת ההודעה" htmlFor="sd-popup-title">
                                            <Input
                                                id="sd-popup-title"
                                                type="text"
                                                value={formData.popupTitle}
                                                onChange={(e) => setFormData(fd => ({ ...fd, popupTitle: e.target.value }))}
                                                placeholder="לדוגמה: ברוכים הבאים לחנות!"
                                            />
                                        </Field>
                                        <Field label="תוכן ההודעה" htmlFor="sd-popup-desc">
                                            <Textarea
                                                id="sd-popup-desc"
                                                value={formData.popupDescription}
                                                onChange={(e) => setFormData(fd => ({ ...fd, popupDescription: e.target.value }))}
                                                rows={3}
                                                placeholder="לדוגמה: משלוחים חינם בהזמנה מעל 200₪ השבוע בלבד."
                                            />
                                        </Field>
                                        {(formData.popupTitle || formData.popupDescription) && (
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <Badge variant="success" size="sm">תוצג ללקוחות</Badge>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="xs"
                                                    onClick={() => setFormData(fd => ({ ...fd, popupTitle: '', popupDescription: '' }))}
                                                    className="text-danger-600 hover:text-danger-700"
                                                >
                                                    נקה הודעה
                                                </Button>
                                            </div>
                                        )}
                                    </Block>

                                    <div className="border-t border-line-subtle" />

                                    <Block
                                        icon={Sparkles}
                                        title="סגנון הצגת ההודעה"
                                        description="בחר כיצד תוצג הודעת קבלת הפנים: חלונית קלאסית או אנימציה עם דמות חמודה."
                                    >
                                        <WelcomeStylePicker
                                            value={formData.popupStyle}
                                            onChange={(popupStyle) => setFormData(fd => ({ ...fd, popupStyle }))}
                                        />
                                    </Block>

                                    <div className="border-t border-line-subtle" />

                                    <Block
                                        icon={CheckCircle2}
                                        title="הודעת אישור רכישה"
                                        description="תיכלל במייל שהלקוחות מקבלים לאחר ביצוע הזמנה."
                                    >
                                        <Textarea
                                            value={formData.purchaseConfirmationMessage}
                                            onChange={(e) => {
                                                setPurchaseMessageDirty(true);
                                                setFormData(fd => ({ ...fd, purchaseConfirmationMessage: e.target.value }));
                                            }}
                                            rows={4}
                                            placeholder={"תודה על הרכישה! ההזמנה שלך מתקבלת ותטופל בהקדם.\n\nאם יש לך שאלות, אל תהסס לפנות אלינו."}
                                        />
                                        {formData.purchaseConfirmationMessage && (
                                            <Badge variant="success" size="sm">יוצג בהודעת אישור ההזמנה ללקוח</Badge>
                                        )}
                                    </Block>
                                </div>
                            )}

                            {/* ---------------------------------------------- FAQ */}
                            {activeSubTab === 'faq' && (
                                <Block
                                    icon={HelpCircle}
                                    title="שאלות נפוצות"
                                    description="שאלות ותשובות שיוצגו ללקוחות בעמוד החנות"
                                    tone="info"
                                >
                                    {faqError && (
                                        <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-800 text-sm">
                                            {faqError}
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {faqList.length === 0 ? (
                                            <div className="text-center py-10 rounded-xl border border-dashed border-line bg-surface-muted">
                                                <HelpCircle className="w-8 h-8 mx-auto text-ink-faint" />
                                                <p className="text-sm text-ink-muted mt-2">אין שאלות נפוצות עדיין</p>
                                                <p className="text-xs text-ink-faint mt-0.5">הוסף שאלה ראשונה למטה</p>
                                            </div>
                                        ) : (
                                            faqList.map((faq) => (
                                                <div key={faq.faq_id} className="rounded-xl border border-line bg-surface p-4">
                                                    {editingFaqId === faq.faq_id ? (
                                                        <div className="space-y-3">
                                                            <input
                                                                type="text"
                                                                defaultValue={faq.question}
                                                                id={`edit-question-${faq.faq_id}`}
                                                                className={inputCls + ' font-semibold'}
                                                                placeholder="שאלה"
                                                            />
                                                            <textarea
                                                                defaultValue={faq.answer}
                                                                id={`edit-answer-${faq.faq_id}`}
                                                                className={inputCls}
                                                                rows="3"
                                                                placeholder="תשובה"
                                                            />
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    icon={<Save />}
                                                                    loading={faqLoading}
                                                                    onClick={() => {
                                                                        const question = document.getElementById(`edit-question-${faq.faq_id}`).value;
                                                                        const answer = document.getElementById(`edit-answer-${faq.faq_id}`).value;
                                                                        handleUpdateFaq(faq.faq_id, { question, answer });
                                                                    }}
                                                                >
                                                                    שמור
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="subtle"
                                                                    disabled={faqLoading}
                                                                    onClick={() => setEditingFaqId(null)}
                                                                >
                                                                    ביטול
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-ink-strong mb-1">ש: {faq.question}</p>
                                                                <p className="text-sm text-ink-muted whitespace-pre-wrap">ת: {faq.answer}</p>
                                                            </div>
                                                            <div className="flex gap-1 shrink-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditingFaqId(faq.faq_id)}
                                                                    disabled={faqLoading}
                                                                    className="p-2 text-brand-600 hover:bg-brand-50 rounded-lg transition-all disabled:opacity-50"
                                                                    title="ערוך"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteFaq(faq.faq_id)}
                                                                    disabled={faqLoading}
                                                                    className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-all disabled:opacity-50"
                                                                    title="מחק"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="rounded-xl border border-line bg-surface-muted p-4 space-y-3">
                                        <p className="text-sm font-semibold text-ink">הוסף שאלה חדשה</p>
                                        <Input
                                            type="text"
                                            value={newFaq.question}
                                            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                                            placeholder="שאלה (לדוגמה: מה שעות הפעילות שלכם?)"
                                        />
                                        <Textarea
                                            value={newFaq.answer}
                                            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                                            rows={3}
                                            placeholder="תשובה (לדוגמה: אנחנו פתוחים מ-8:00 עד 20:00)"
                                        />
                                        <Button
                                            type="button"
                                            fullWidth
                                            icon={<Plus />}
                                            loading={faqLoading}
                                            disabled={!newFaq.question.trim() || !newFaq.answer.trim()}
                                            onClick={handleAddFaq}
                                        >
                                            הוסף שאלה
                                        </Button>
                                    </div>
                                </Block>
                            )}

                            {/* ---------------------------------------------- CANCELLATION */}
                            {activeSubTab === 'cancellation' && (
                                <Block
                                    icon={ScrollText}
                                    title="מדיניות ביטול רכישה"
                                    description='תוצג ללקוחות בלשונית ייעודית בחנות. השאר ריק כדי לא להציג מדיניות.'
                                    tone="warning"
                                >
                                    <Field label="תוכן המדיניות" htmlFor="sd-cancel" help='לחץ "שמור שינויים" כדי לעדכן את המדיניות.'>
                                        <Textarea
                                            id="sd-cancel"
                                            value={formData.cancellationPolicy}
                                            onChange={(e) => {
                                                setCancellationPolicyDirty(true);
                                                setFormData(fd => ({ ...fd, cancellationPolicy: e.target.value }));
                                            }}
                                            rows={8}
                                            placeholder={"לדוגמה:\n• ביטול אפשרי עד 24 שעות לפני המשלוח.\n• החזר כספי מלא יבוצע תוך 14 ימי עסקים.\n• מוצרים מתכלים אינם ניתנים להחזרה."}
                                        />
                                    </Field>
                                    {formData.cancellationPolicy ? (
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <Badge variant="success" size="sm">תוצג ללקוחות בלשונית "מדיניות ביטול"</Badge>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="xs"
                                                icon={<Trash2 />}
                                                className="text-danger-600 hover:text-danger-700"
                                                onClick={() => {
                                                    if (!window.confirm('להסיר את מדיניות הביטול? הלשונית תיעלם מהחנות עד שתוסיף מדיניות חדשה.')) return;
                                                    setCancellationPolicyDirty(true);
                                                    setFormData(fd => ({ ...fd, cancellationPolicy: '' }));
                                                }}
                                            >
                                                הסר מדיניות
                                            </Button>
                                        </div>
                                    ) : (
                                        <Badge variant="neutral" size="sm">אין מדיניות ביטול. הוסף טקסט והקש "שמור שינויים".</Badge>
                                    )}
                                </Block>
                            )}

                            {/* ---------------------------------------------- CUSTOMERS CLUB */}
                            {activeSubTab === 'club' && (
                                <div className="space-y-8">
                                    <Block
                                        icon={Gift}
                                        title="הטבה ללקוחות מועדון"
                                        description="בחרו את סוג ההטבה שלקוחות רשומים יקבלו. ההטבה תוצג בחנות לצד כפתור ההרשמה. בחרו 'ללא' כדי להסתיר את הכפתור."
                                        tone="brand"
                                    >
                                        {/* Benefit-type picker */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {CLUB_BENEFIT_TYPES.map((t) => {
                                                const active = clubBenefit.type === t.id;
                                                return (
                                                    <button
                                                        key={t.id}
                                                        type="button"
                                                        onClick={() => setBenefitType(t.id)}
                                                        className={`text-start rounded-xl border p-3 transition-all ${active ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-200' : 'border-line bg-surface hover:border-line-strong hover:bg-surface-muted'}`}
                                                    >
                                                        <span className="block font-semibold text-ink">{t.label}</span>
                                                        <span className="block text-xs text-ink-subtle mt-0.5">{t.hint}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Per-type configuration */}
                                        {clubBenefit.type === 'coupon' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                                <Field label="אחוז הנחה (%)" htmlFor="club-coupon-pct">
                                                    <Input id="club-coupon-pct" type="number" min="1" max="100"
                                                        value={clubBenefit.discount_percent ?? ''}
                                                        onChange={(e) => setBenefitNumber('discount_percent', e.target.value)}
                                                        placeholder="לדוגמה: 10" />
                                                </Field>
                                                <Field label="מספר שימושים" htmlFor="club-coupon-uses">
                                                    <Input id="club-coupon-uses" type="number" min="1"
                                                        value={clubBenefit.max_uses ?? ''}
                                                        onChange={(e) => setBenefitNumber('max_uses', e.target.value)}
                                                        placeholder="לדוגמה: 50" />
                                                </Field>
                                            </div>
                                        )}

                                        {clubBenefit.type === 'referral' && (
                                            <div className="space-y-3 pt-1">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <Field label="מספר אנשים שאפשר להעביר להם" htmlFor="club-ref-uses">
                                                        <Input id="club-ref-uses" type="number" min="1"
                                                            value={clubBenefit.max_uses ?? ''}
                                                            onChange={(e) => setBenefitNumber('max_uses', e.target.value)}
                                                            placeholder="לדוגמה: 5" />
                                                    </Field>
                                                    <Field label="הנחה למזמין על כל מימוש (%)" htmlFor="club-ref-pct">
                                                        <Input id="club-ref-pct" type="number" min="1" max="100"
                                                            value={clubBenefit.referral_discount_percent ?? ''}
                                                            onChange={(e) => setBenefitNumber('referral_discount_percent', e.target.value)}
                                                            placeholder="לדוגמה: 5" />
                                                    </Field>
                                                </div>
                                                <HelpText>הקופון ניתן להעברה למספר אנשים. בכל מימוש המזמין צובר הנחה — שמסתכמת להנחה גדולה יותר.</HelpText>
                                            </div>
                                        )}

                                        {clubBenefit.type === 'punch_card' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                                <Field label="כל כמה רכישות (הרכישה ה-X במתנה)" htmlFor="club-punch-x">
                                                    <Input id="club-punch-x" type="number" min="2"
                                                        value={clubBenefit.purchases_required ?? ''}
                                                        onChange={(e) => setBenefitNumber('purchases_required', e.target.value)}
                                                        placeholder="לדוגמה: 10" />
                                                </Field>
                                                <Field label="שווי מקסימלי למתנה (₪, 0 = ללא הגבלה)" htmlFor="club-punch-max">
                                                    <Input id="club-punch-max" type="number" min="0"
                                                        value={clubBenefit.max_free_value ?? ''}
                                                        onChange={(e) => setBenefitNumber('max_free_value', e.target.value)}
                                                        placeholder="לדוגמה: 50" />
                                                </Field>
                                            </div>
                                        )}

                                        {clubBenefit.type === 'other' && (
                                            <Field label="תיאור ההטבה" htmlFor="club-other-desc">
                                                <Textarea id="club-other-desc" rows={4}
                                                    value={clubBenefit.description ?? ''}
                                                    onChange={(e) => setBenefitField({ description: e.target.value })}
                                                    placeholder={"לדוגמה:\nמתנה ביום ההולדת + משלוח חינם ללקוחות מועדון."} />
                                            </Field>
                                        )}

                                        {/* Status row */}
                                        <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                                            {hasClubBenefit(clubBenefit) ? (
                                                <Badge variant="success" size="sm">כפתור הצטרפות יוצג ללקוחות בחנות</Badge>
                                            ) : (
                                                <Badge variant="neutral" size="sm">השלימו את פרטי ההטבה והקישו "שמור שינויים".</Badge>
                                            )}
                                            {clubBenefit.type && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="xs"
                                                    icon={<Trash2 />}
                                                    className="text-danger-600 hover:text-danger-700"
                                                    onClick={() => {
                                                        if (!window.confirm('להסיר את הטבת המועדון? כפתור ההרשמה ייעלם מהחנות.')) return;
                                                        setBenefitType('');
                                                    }}
                                                >
                                                    הסר הטבה
                                                </Button>
                                            )}
                                        </div>
                                        {hasClubBenefit(clubBenefit) && (
                                            <p className="text-xs text-ink-subtle">בחנות יוצג: {clubBenefitDescription(clubBenefit)}</p>
                                        )}
                                    </Block>

                                    <div className="border-t border-line-subtle" />

                                    <Block
                                        icon={Users}
                                        title="לקוחות רשומים"
                                        description="הלקוחות שנרשמו למועדון בחנות שלך."
                                        tone="info"
                                    >
                                        {clubMembersLoading ? (
                                            <p className="text-sm text-ink-muted">טוען לקוחות…</p>
                                        ) : clubMembers.length === 0 ? (
                                            <Badge variant="neutral" size="sm">עדיין אין לקוחות רשומים.</Badge>
                                        ) : (
                                            <>
                                                <Badge variant="success" size="sm">{clubMembers.length} לקוחות רשומים</Badge>
                                                <ul className="mt-3 divide-y divide-line-subtle rounded-xl border border-line bg-surface-muted/40">
                                                    {clubMembers.map((m) => (
                                                        <li key={m.member_id} className="px-4 py-2.5 space-y-1">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <span className="font-medium text-ink truncate">{m.name || '—'}</span>
                                                                {m.phone ? (
                                                                    <a href={`tel:${m.phone}`} className="text-sm text-brand-700 hover:text-brand-800 font-mono shrink-0" dir="ltr">{m.phone}</a>
                                                                ) : m.email ? (
                                                                    <a href={`mailto:${m.email}`} className="text-sm text-brand-700 hover:text-brand-800 shrink-0 truncate" dir="ltr">{m.email}</a>
                                                                ) : (
                                                                    <span className="text-sm text-ink-subtle shrink-0">—</span>
                                                                )}
                                                            </div>
                                                            {m.referral && (
                                                                <div className="flex items-center gap-2 flex-wrap text-xs">
                                                                    <span className="font-mono font-bold text-ink bg-surface px-2 py-0.5 rounded border border-line" dir="ltr">{m.referral.code}</span>
                                                                    <span className="text-ink-muted">{m.referral.uses}/{m.referral.max_uses} מימושים</span>
                                                                    <span className="text-ink-muted">•</span>
                                                                    <span className="text-success-700 font-semibold">{m.referral.credit_percent}% הנחה צבורה</span>
                                                                </div>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}
                                    </Block>

                                    {/* Referral redemption log — full usage tracking */}
                                    {isReferralClub && (
                                        <>
                                            <div className="border-t border-line-subtle" />
                                            <Block
                                                icon={Ticket}
                                                title="מעקב מימושי קופון הפניה"
                                                description="כל מימוש של קופון הפניה — מי מימש, מתי, ולמי נזקף."
                                                tone="brand"
                                            >
                                                {clubMembersLoading ? (
                                                    <p className="text-sm text-ink-muted">טוען…</p>
                                                ) : clubRedemptions.length === 0 ? (
                                                    <Badge variant="neutral" size="sm">עדיין אין מימושים.</Badge>
                                                ) : (
                                                    <ul className="divide-y divide-line-subtle rounded-xl border border-line bg-surface-muted/40">
                                                        {clubRedemptions.map((r, i) => (
                                                            <li key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                                                                <span className="min-w-0">
                                                                    <span className="font-medium text-ink">{r.redeemer_name || '—'}</span>
                                                                    <span className="text-ink-subtle">
                                                                        {' '}{r.role === 'referrer' ? 'מימש/ה הנחה צבורה' : 'השתמש/ה בקוד'}
                                                                        {' '}<span className="font-mono" dir="ltr">{r.code}</span>
                                                                    </span>
                                                                </span>
                                                                <span className="shrink-0 flex items-center gap-2">
                                                                    <Badge variant={r.role === 'referrer' ? 'brand' : 'success'} size="sm">{r.discount_percent}%</Badge>
                                                                    <span className="text-xs text-ink-subtle">{formatRedemptionDate(r.created_at)}</span>
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </Block>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ---------------------------------------------- CONTACT & NOTIFICATIONS */}
                            {activeSubTab === 'contact' && (
                                <div className="space-y-8">
                                    <Block
                                        icon={Phone}
                                        title="פרטי יצירת קשר"
                                        description="מספר טלפון ללקוחות שמעוניינים ליצור איתך קשר. יוצג בחנות לצד הטאבים."
                                        tone="success"
                                    >
                                        <Field label="מספר טלפון" htmlFor="sd-phone">
                                            <Input
                                                id="sd-phone"
                                                type="tel"
                                                value={formData.contactPhone}
                                                onChange={(e) => setFormData(fd => ({ ...fd, contactPhone: e.target.value }))}
                                                placeholder="לדוגמה: 050-1234567"
                                            />
                                        </Field>
                                        <label htmlFor="whatsapp-available" className="flex items-start gap-3 rounded-xl border border-line bg-surface-muted p-4 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="whatsapp-available"
                                                checked={formData.whatsappAvailable}
                                                onChange={(e) => setFormData(fd => ({ ...fd, whatsappAvailable: e.target.checked }))}
                                                className="mt-0.5 w-4 h-4 text-success-600 border-line-strong rounded focus:ring-2 focus:ring-success-500"
                                            />
                                            <span className="flex-1 text-sm font-medium text-ink">
                                                המספר זמין גם בוואטסאפ
                                                <span className="block text-xs text-ink-subtle font-normal mt-0.5">
                                                    סמן כאן אם ניתן ליצור איתך קשר גם דרך וואטסאפ במספר זה
                                                </span>
                                            </span>
                                        </label>
                                        {formData.contactPhone && (
                                            <Badge variant="success" size="sm">
                                                יוצג ללקוחות: {formData.contactPhone}{formData.whatsappAvailable && ' (כולל וואטסאפ)'}
                                            </Badge>
                                        )}
                                        <p className="text-xs text-ink-subtle">פרטי הקשר נשמרים יחד עם שאר ההגדרות בלחיצה על "שמור שינויים".</p>
                                    </Block>

                                    <div className="border-t border-line-subtle" />

                                    <Block
                                        icon={Send}
                                        title="קבלת התראות בטלגרם"
                                        description="קבלו התראות מיידיות בכל פעם שנכנסת הזמנה חדשה לחנות."
                                        tone="info"
                                    >
                                        <ul className="text-sm text-ink-muted space-y-1 pr-4 list-disc">
                                            <li>התראות בזמן אמת על הזמנות חדשות</li>
                                            <li>הרשמה מהירה בלחיצת כפתור</li>
                                        </ul>
                                        <Button
                                            type="button"
                                            variant="primary"
                                            fullWidth
                                            icon={<Send />}
                                            disabled={!store?.store_id}
                                            onClick={() => {
                                                const botUsername = 'MekomitBot';
                                                const storeId = store?.store_id || '';
                                                const telegramUrl = `https://t.me/${botUsername}?start=r_${storeId}`;
                                                window.open(telegramUrl, '_blank', 'noopener,noreferrer');
                                            }}
                                        >
                                            הירשמו לקבלת התראות בטלגרם
                                        </Button>
                                        <p className="text-xs text-ink-subtle text-center">הכפתור יפתח את הבוט בטלגרם לצורך השלמת ההרשמה</p>
                                    </Block>
                                </div>
                            )}

                            {/* ---------------------------------------------- MANAGEMENT */}
                            {activeSubTab === 'management' && isActualOwner && (
                                <div className="space-y-8">
                                    <Block
                                        icon={UserPlus}
                                        title="הזמן מנהל משנה"
                                        description="שתף את החנות עם משתמש נוסף והפוך אותו למנהל משנה. הקישור תקף ל-5 דקות בלבד."
                                        tone="info"
                                    >
                                        {inviteError && (
                                            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-800 text-sm">
                                                {inviteError}
                                            </div>
                                        )}
                                        <Button
                                            type="button"
                                            fullWidth
                                            icon={<UserPlus />}
                                            loading={generatingInvite}
                                            disabled={!store?.store_id}
                                            onClick={handleGenerateInvite}
                                        >
                                            {generatingInvite ? 'יוצר קישור…' : 'צור קישור הזמנה'}
                                        </Button>

                                        {inviteLink && (
                                            <div className="rounded-xl border border-success-200 bg-success-50/60 p-4 space-y-3 animate-fade-in">
                                                <div className="flex items-center gap-2 text-success-700 font-medium text-sm">
                                                    <CheckCircle2 className="w-5 h-5" />
                                                    <span>קישור הזמנה נוצר בהצלחה!</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={inviteLink}
                                                        readOnly
                                                        className="flex-1 px-3 py-2 bg-surface border border-line rounded-lg text-sm text-ink font-mono"
                                                    />
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={inviteLinkCopied ? 'success' : 'outline'}
                                                        icon={inviteLinkCopied ? <CheckCircle2 /> : <Copy />}
                                                        onClick={handleCopyInviteLink}
                                                    >
                                                        {inviteLinkCopied ? 'הועתק!' : 'העתק'}
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-warning-700 bg-warning-50 border border-warning-200 px-3 py-2 rounded-lg">
                                                    <strong>שים לב:</strong> קישור זה יפוג תוך 5 דקות. שתף אותו עם המשתמש המוזמן מיד.
                                                </p>
                                            </div>
                                        )}
                                    </Block>

                                    {store?.co_admins && store.co_admins.length > 0 && (
                                        <>
                                            <div className="border-t border-line-subtle" />
                                            <Block
                                                icon={UserMinus}
                                                title="מנהלי משנה"
                                                description="משתמשים המורשים לנהל את החנות יחד איתך"
                                            >
                                                <div className="space-y-2">
                                                    {store.co_admins.map((admin) => (
                                                        <div
                                                            key={admin.user_id}
                                                            className="flex items-center justify-between gap-3 p-4 rounded-xl border border-line bg-surface"
                                                        >
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <div className="w-10 h-10 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                                                                    {admin.name.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="font-semibold text-ink-strong truncate">{admin.name}</p>
                                                                    <p className="text-xs text-ink-subtle truncate">מזהה: {admin.user_id}</p>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="danger"
                                                                icon={<UserMinus />}
                                                                loading={removingCoAdmin === admin.user_id}
                                                                onClick={() => handleRemoveCoAdmin(admin.user_id, admin.name)}
                                                            >
                                                                הסר
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Block>
                                        </>
                                    )}

                                    {/* Danger zone */}
                                    <div className="border-t border-line-subtle" />
                                    <div className="rounded-xl border border-danger-200 bg-danger-50/50 p-5">
                                        <div className="flex items-start gap-3">
                                            <span className="shrink-0 p-2.5 rounded-xl ring-1 bg-danger-50 text-danger-600 ring-danger-100">
                                                <AlertTriangle className="w-5 h-5" />
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-base font-bold text-danger-800">אזור מסוכן</h3>
                                                <p className="text-sm text-danger-700 mt-0.5">
                                                    מחיקת החנות תמחק את כל המוצרים, ההזמנות והנתונים המשויכים. פעולה זו אינה הפיכה.
                                                </p>
                                                <Button
                                                    type="button"
                                                    variant="danger"
                                                    icon={<Trash2 />}
                                                    loading={deleting}
                                                    onClick={handleDeleteStore}
                                                    className="mt-4"
                                                >
                                                    {deleting ? 'מוחק…' : 'מחק חנות לצמיתות'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* Sticky save bar — only for sections that edit the store form.
                         * Floats ABOVE the fixed FooterNav (z-nav=40) at z-floating, and on
                         * mobile is inset on both sides so it never sits beneath the corner
                         * FABs (AI-chat at right z-[10001], RefreshFab at left) whose taps
                         * would otherwise swallow the Save button. */}
                        {showSaveBar && (
                            <>
                                {/* Scroll spacer so the floating bar never hides the last fields on mobile */}
                                <div aria-hidden="true" className="h-16 lg:hidden" />
                                <div
                                    className="sticky z-floating px-16 sm:px-0"
                                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.75rem)' }}
                                >
                                    <Card variant="elevated" padding="sm" radius="xl" className="flex items-center justify-between gap-3">
                                        <p className="text-xs text-ink-muted hidden sm:block px-2">
                                            השינויים נשמרים יחד. לחץ שמור כדי לעדכן את החנות.
                                        </p>
                                        <Button
                                            type="submit"
                                            size="md"
                                            icon={<Save />}
                                            loading={loading}
                                            className="w-full sm:w-auto"
                                        >
                                            {loading ? 'שומר…' : 'שמור שינויים'}
                                        </Button>
                                    </Card>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            </div>

            {showCropModal && tempImageSrc && (
                <StoreImageCropModal
                    imageSrc={tempImageSrc}
                    onClose={() => {
                        setShowCropModal(false);
                        setTempImageSrc(null);
                        setCropImageType(null);
                    }}
                    onCropComplete={handleCropComplete}
                    {...({
                        profile:               { aspect: 1,      mode: 'profile' },
                        cover:                 { aspect: 16 / 9, mode: 'cover' },
                        about:                 { aspect: 1,      mode: 'free' },
                        background:            {
                            aspect: 16 / 9,
                            mode: 'crop',
                            title: 'חיתוך רקע למסך רחב (16:9)',
                            hint: 'התמונה תיחתך ליחס רחב (16:9) ותוצג ברקע במסכי דסקטופ',
                        },
                        'background-portrait': {
                            aspect: 9 / 16,
                            mode: 'crop',
                            title: 'חיתוך רקע למובייל (9:16)',
                            hint: 'התמונה תיחתך ליחס לאורך (9:16) ותוצג ברקע במסכי מובייל',
                        },
                    }[cropImageType] || { aspect: 1, mode: 'profile' })}
                />
            )}
        </div>
    );
};
